import React, { useEffect, useRef, useState } from 'react';
import { Redirect, Switch, useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import { useAuth0, useAuth0Data } from 'neon-auth0';
import { useMediaQuery } from '@material-ui/core';
import { version } from '../package.json';
import { getPlatformInfo, useLogger, sessionId } from './components/providers/logger/Logger';
import { LogAction, LogLevel } from './components/providers/logger/logger-types';
import { useUser } from './components/providers/UserProvider';
import { ResearchRole } from './shared/global-enums';
import PublisherContent from './components/containers/admin/PublisherContent';
import AuthenticatedRoute from './components/ui/AuthenticatedRoute';
import { useMutation, useQuery } from '@apollo/react-hooks';
import ResearchAppBar from './components/ui/ResearchAppBar';
import ClientContainer from './components/containers/client/ClientContainer';
import { useUpBreakpoints } from './components/providers/BreakpointsProvider';
import { clientRoutes, fullScreenRoutes, routesWithoutHeader, Route as AppRoute } from './routes'
import gql from 'graphql-tag';
import { Loader, useTheme } from 'neon-design-system';
import { redirectToCompleteProfile } from 'neon-utils';
import { config } from './config';
import { redirectToUrl } from './index';
import { UPDATE_USER_FROM_TOKEN } from './gql/users';
import { IsUserProfileComplete, User } from './shared/types';
import { ViewTrackerProvider } from './components/providers/ViewTrackerProvider';
import jwt from 'jwt-decode';
import * as appcues from 'neon-appcues';
import { baseUrls, getUrlsRec } from './utils/baseUrls.utils';
import { VersionChecker } from './components/VersionChecker';
import 'animate.css'
import { getArticleLink } from './utils/link.utils';

export const IS_USER_PROFILE_COMPLETE = gql`
	query isUserProfileComplete {
		document: isUserProfileComplete {
			isProfileComplete
		}
	}`;

let switcherListener: any;
let clientListener: any;
let clientLogListener: any;
let articleListener: any;

type ArticleEventDetails = {
	id: string;
    title: string;
    publishedOn: Date;
    metadata?: {
		url: string;
		publishedBy: string;
	};
};

const App: React.FC = (): JSX.Element | null => {
	const { logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
	const { idToken, accessToken } = useAuth0Data();
	const location = useLocation();
	const { updateIdentity, identity, printLog } = useLogger();
	const { hasScope, hasIntelligenceScope, userDetails } = useUser();

	const { theme } = useTheme();
	const hideXS = useMediaQuery(theme.breakpoints.up('sm'));
	const intelligenceEnabled = hasIntelligenceScope() && config.intelligenceAccessEnabled && hideXS;

	const { loading: isProfileCompletedLoading } = useQuery<{ document: IsUserProfileComplete }>(IS_USER_PROFILE_COMPLETE, {
		fetchPolicy: 'network-only',
		onCompleted(response) {
			if (!response.document.isProfileComplete) {
				redirectToCompleteProfile(config.landingPageUrl, window.location.href);
			}
		}
	});
	const history = useHistory();
	const { md } = useUpBreakpoints();

	const [updateUserFromToken] = useMutation(UPDATE_USER_FROM_TOKEN);
	const fullScreenRouteMatch = useRouteMatch(fullScreenRoutes.map(r => r.path));
	const routeWithoutHeaderMatch = useRouteMatch(routesWithoutHeader.map(r => r.path));
	const allRoutes = getUrlsRec(baseUrls);
	const hasRouteMatch = useRouteMatch(allRoutes);
	const [miSwitcher, setSwitcher] = useState<HTMLDivElement | null>(null);
	const [miClient, setClient] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		const redirectUrls = [
			baseUrls.browse.main,
			`${baseUrls.browse.main}/`,
			baseUrls.topics,
			`${baseUrls.topics}/`
		];
		if (redirectUrls.indexOf(location.pathname) > -1) {
			history.push(baseUrls.channels.main);
		}
	}, [location]);

	useEffect(() => {
		if(!hasRouteMatch) {
			history.push(baseUrls.forYou);
		}
	}, [hasRouteMatch, history]);

	const menuItems = [];

	if ([ResearchRole.Administrator, ResearchRole.Contributor, ResearchRole.Editor, ResearchRole.Subscriber].some(hasScope)) {
		menuItems.push({ name: 'For you', link: baseUrls.forYou });
		menuItems.push({ name: 'Channels', link: baseUrls.channels.main, isActive: location.pathname.includes(baseUrls.browse.main) });
		menuItems.push({ name: 'Subscriptions', link: baseUrls.subscriptions, isActive: location.pathname.includes(baseUrls.emailNotifications) });
	}

	if (md && [ResearchRole.Administrator, ResearchRole.Contributor, ResearchRole.Editor].some(hasScope)) {
		menuItems.push({ name: 'Publisher\'s Dashboard', link: baseUrls.publisher.main });
	}

	const isPublisherRoutes = location.pathname.includes(baseUrls.publisher.main);

	const client = document.getElementById('client');
	const switcher = document.getElementById('switcher');

	const isInteligenceClientOpen = localStorage.getItem('InteligenceClient') === 'true' || 
		localStorage.getItem('InteligenceClient') === null;

	useEffect(() => {
		if (!intelligenceEnabled) return;

		if (isInteligenceClientOpen) {
			localStorage.setItem('InteligenceClient', 'true');

			if (client) {
				client.style.visibility = 'visible';
				client.style.right = '40px';
			}
		} else {
			if (client) client.classList.add('animate__fadeOutRight');
			if (switcher) switcher.style.visibility = 'visible';
		}
	}, [intelligenceEnabled, isInteligenceClientOpen, client, switcher])

	useEffect(() => {
		if (!intelligenceEnabled) return;
		if (!miSwitcher) return;
		if (switcherListener) {
			miSwitcher.removeEventListener('toggle', switcherListener);
		}
		switcherListener = () => {
			if (client && switcher) {
				client.style.visibility = 'visible';
				switcher.classList.remove('animate__zoomIn');
				switcher.classList.add('animate__zoomOut');
				client.classList.remove('animate__fadeOutRight');
				client.classList.add('animate__fadeInRight');
				client.style.right = '40px';

				localStorage.setItem('InteligenceClient', 'true');
			}
		}
		miSwitcher.addEventListener('toggle', switcherListener, { capture: true });
	}, [miSwitcher, intelligenceEnabled]);

	useEffect(() => {
		if (!intelligenceEnabled) return;
		if (!miClient) return;
		if (clientListener) {
			miClient.removeEventListener('closeChat', clientListener);
		}
		if (clientLogListener) {
			miClient.removeEventListener('log', clientLogListener);
		}
		if (articleListener) {
			miClient.removeEventListener('article', articleListener);
		}
		clientListener = () => {
			if (client && switcher) {
				setTimeout(() => {
					client.classList.remove('animate__fadeInRight');
					client.classList.add('animate__fadeOutRight');
					switcher.classList.remove('animate__zoomOut');
					switcher.classList.add('animate__zoomIn');
					switcher.style.display = 'block';
					client.style.right = '0';

					switcher.style.visibility = 'visible';
					localStorage.setItem('InteligenceClient', 'false');
				}, 500)
			}
		}
		miClient.addEventListener('closeChat', clientListener, { capture: true });
		clientLogListener = (e: CustomEvent<{ message: string; data: any }>) => {
			const { message, data } = e.detail;
			printLog(LogLevel.INFO, LogAction.IntelligenceClient, message, data);
		};
		miClient.addEventListener('log', clientLogListener, { capture: true });
		articleListener = (e: CustomEvent<{ article: ArticleEventDetails }>) => {
			const { article } = e.detail;
			if (article.metadata?.url) {
				printLog(LogLevel.INFO, LogAction.IntelligenceClient, 'Article opened via Intelligence Client', {
					articleId: article.id,
					neonUserId: userDetails?.id
				});
				history.push(getArticleLink(article.id));
			} else {
				window.open(`https://app.neon.markets/insights${getArticleLink(article.id)}`, '_blank');
			}
		};
		miClient.addEventListener('article', articleListener, { capture: true });
	}, [miClient, intelligenceEnabled, userDetails]);

	useEffect(() => {
		const neonUserId = jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];
		updateIdentity(neonUserId);
	}, [accessToken, updateIdentity]);

	useEffect(() => {
		if (!identity) return;
		const platformInfo = getPlatformInfo(version);
		printLog(LogLevel.INFO, LogAction.LoggedIn, 'Logged in to NeonResearch', platformInfo);
		// eslint-disable-next-line
	}, [identity]);

	const hasUpdateUserRun = useRef(false);
	useEffect(() => {
		const token = jwt<any>(accessToken);
		const hasIntelligencePermission = token.scope.split(' ').indexOf('intelligence') > -1;

		const setupAppcues = (user?: User) => {
			appcues.init({
				appName: 'insights',
				accessToken: accessToken,
				getAccessToken: getAccessTokenSilently,
				idToken: idToken.__raw,
				log(message, data) {
					printLog(LogLevel.INFO, LogAction.Appcues, message, data);
				},
				analyticOptions: {
					env: appcues.environmentResolver(config.nodeEnv),
					recordAnalytics: true,
					analyticsOnly: false,
					sessionData: {
						sessionId,
						appName: 'Neon Insights',
						appVersion: version
					}
				}
			});

			const rootCategories = user?.subscriptions?.categories.map(subscription => subscription?.category?.rootCategory?.name) || [];

			appcues.identify({
				insightsRole: user?.role,
				hasAgsSubscription: rootCategories.some(name => name === 'Agriculture'),
				hasEnergySubscription: rootCategories.some(name => name === 'Energy'),
				hasMetalsSubscription: rootCategories.some(name => name === 'Metals'),
				hasIntelligencePermission,
				subscribedToPremiumChannel: user?.channelSubscriptions.some(cs => cs.subscriptionState === 'active'),
				...(user?.channelSubscriptions.reduce((map, cs) => ({ ...map, [`subscribedToChannel-${cs.channelId}-${cs.channel?.name}`]: cs.subscriptionState === 'active' }), {}))
			});

			appcues.analyticsLog({});
			history.listen(appcues.page);
			history.listen((_, __) => appcues.analyticsLog({}));
		};

		if (isAuthenticated && idToken && !hasUpdateUserRun.current) {
			hasUpdateUserRun.current = true;
			updateUserFromToken({ variables: { idToken: idToken.__raw } }).then(x => {
				setupAppcues(x.data?.updateUserFromToken?.user);
				if (
					x?.data?.updateUserFromToken?.isNewUser
					&& !window.location.href.includes('browse/article')
					&& !x?.data?.user?.channelSubscriptions?.length
				) {
					history.push(baseUrls.forYou);
				}
			});
		}
	}, [isAuthenticated, idToken, accessToken, updateUserFromToken, history, getAccessTokenSilently, printLog, intelligenceEnabled]);

	if (isProfileCompletedLoading) return <Loader />;

	if (fullScreenRouteMatch) {
		return (
			<ViewTrackerProvider>
				<Switch>
					{fullScreenRoutes.map((r: AppRoute<string>, i: number) => <AuthenticatedRoute exact key={i + location.pathname} {...r} />)}
				</Switch>
			</ViewTrackerProvider>
		);
	}

	if (routeWithoutHeaderMatch) {
		return (
			<ViewTrackerProvider>
				<ClientContainer withHeader={false}>
					<Switch>
						{clientRoutes.map((r: AppRoute<string>, i: number) => <AuthenticatedRoute exact key={i + location.pathname} {...r} />)}
					</Switch>
				</ClientContainer>
			</ViewTrackerProvider>
		);
	}

	return (
		<>
			{isAuthenticated && <ResearchAppBar
				userDetails={userDetails}
				menuItems={menuItems}
				onSignOut={(): void => {
					logout({ returnTo: redirectToUrl()?.href });
					printLog(LogLevel.INFO, LogAction.LoggedOut, 'Logged out');
				}}
			/>}

			{
				intelligenceEnabled && !isPublisherRoutes ? (
					<>
						<div ref={setSwitcher} id="switcher" className="animate__animated switcher_default_duration" style={{ visibility: 'hidden' }}>
							<marex-intelligence-switcher></marex-intelligence-switcher>
						</div>

						<div ref={setClient} id="client" className="animate__animated chat_default_duration animate__fadeInRight" style={{ visibility: 'hidden' }}>
							<marex-intelligence-client token={accessToken}></marex-intelligence-client>
						</div>
					</>
				) : <></>
			}

			<VersionChecker />
			<Switch>
				<Redirect
					exact
					from="/"
					to={md && [ResearchRole.Administrator, ResearchRole.Contributor, ResearchRole.Editor].some(hasScope) ?
						baseUrls.publisher.main : baseUrls.forYou}
				/>
				<AuthenticatedRoute path={baseUrls.publisher.main} component={PublisherContent} permittedRoles={[ResearchRole.Administrator, ResearchRole.Editor, ResearchRole.Contributor]} />
				<ClientContainer>
					<Switch>
						{clientRoutes.map((r: AppRoute<string>, i: number) => <AuthenticatedRoute exact key={i + location.pathname} {...r} />)}
					</Switch>
				</ClientContainer>
			</Switch>
		</>
	);
};

export default App;
